import { useState, useRef, useEffect } from 'react';
import { verifyMfa, resendMfa } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { LayoutDashboard, ShieldCheck, Mail, Phone, MessageSquare, RefreshCw } from 'lucide-react';

const METHOD_META = {
  email: { icon: Mail, label: 'Email', desc: 'Enter the code sent to your email' },
  sms: { icon: MessageSquare, label: 'SMS', desc: 'Enter the code sent via text message' },
  phone: { icon: Phone, label: 'Phone Call', desc: 'Enter the code from the phone call' },
};

export default function MfaVerifyPage({ challengeId, mfaMethod, emailHint, phoneHint, onSuccess }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [currentChallengeId, setCurrentChallengeId] = useState(challengeId);
  const inputRefs = useRef([]);
  const meta = METHOD_META[mfaMethod] || METHOD_META.email;
  const MethodIcon = meta.icon;

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      pasted.split('').forEach((ch, i) => { if (i < 6) newCode[i] = ch; });
      setCode(newCode);
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) { toast.error('Please enter the full 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await verifyMfa({ challenge_id: currentChallengeId, code: fullCode });
      toast.success('Verified successfully!');
      onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await resendMfa({ challenge_id: currentChallengeId });
      if (res.data.challenge_id) setCurrentChallengeId(res.data.challenge_id);
      toast.success(`New code sent via ${meta.label}`);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F7F6F2' }}>
      <div className="w-full max-w-md mx-4" data-testid="mfa-verify-page">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#D98A4A' }}>
              <LayoutDashboard size={20} className="text-white" />
            </div>
            <span className="font-heading text-xl font-semibold italic" style={{ color: '#1A1A1A' }}>AI Snugglemind</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #E5E3DB', boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(198,156,109,0.1)' }}>
              <ShieldCheck size={28} style={{ color: '#D98A4A' }} />
            </div>
            <h1 className="font-heading text-2xl font-medium mb-1" style={{ color: '#1A1A1A' }}>Two-Factor Authentication</h1>
            <p className="font-body text-sm" style={{ color: '#4B5563' }}>{meta.desc}</p>
          </div>

          {/* Method indicator */}
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl mb-6" style={{ background: 'rgba(198,156,109,0.06)', border: '1px solid rgba(198,156,109,0.15)' }}>
            <MethodIcon size={16} style={{ color: '#D98A4A' }} />
            <span className="font-body text-xs" style={{ color: '#4B5563' }}>
              Sent to {mfaMethod === 'email' ? (emailHint || 'your email') : (phoneHint || 'your phone')}
            </span>
          </div>

          {/* Code inputs */}
          <div className="flex justify-center gap-2.5 mb-6" onPaste={handlePaste}>
            {code.map((digit, i) => (
              <input
                key={`mfa-digit-${i}`}
                ref={(el) => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                data-testid={`mfa-code-input-${i}`}
                className="w-12 h-14 text-center text-xl font-mono font-semibold rounded-xl outline-none transition-all duration-200 focus:ring-2"
                style={{
                  border: digit ? '2px solid #D98A4A' : '1px solid #E5E3DB',
                  background: '#FFFFFF',
                  color: '#1A1A1A',
                  boxShadow: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#D98A4A'; e.target.style.boxShadow = '0 0 0 3px rgba(198,156,109,0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = digit ? '#D98A4A' : '#E5E3DB'; e.target.style.boxShadow = 'none'; }}
              />
            ))}
          </div>

          <Button
            onClick={handleVerify}
            disabled={loading || code.join('').length !== 6}
            className="w-full h-12 rounded-full font-body font-medium text-white transition-colors duration-200"
            style={{ background: code.join('').length === 6 ? '#C84B31' : '#9CA3AF' }}
            data-testid="mfa-verify-btn"
          >
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </Button>

          <div className="mt-5 text-center">
            <button
              onClick={handleResend}
              disabled={resending}
              className="font-body text-sm inline-flex items-center gap-1.5 transition-colors"
              style={{ color: '#D98A4A' }}
              data-testid="mfa-resend-btn"
            >
              <RefreshCw size={14} className={resending ? 'animate-spin' : ''} />
              {resending ? 'Sending...' : "Didn't receive a code? Resend"}
            </button>
          </div>
        </div>

        <p className="mt-6 text-center font-body text-xs" style={{ color: '#9CA3AF' }}>
          Code expires in 10 minutes
        </p>
      </div>
    </div>
  );
}
